// Client configuration and RFC 7591 manifests, using only the Foundation's HTTP contract.
import type { FetchLike } from "@emdur/ui";
import validateManifest from "./clientContract.generated";
import type { Patch } from "./mergePatch";
import type { Proposal } from "./proposals";
import { publicKeySet, publicHttpsAddress } from "./webapp";
import { patchVersioned, readVersioned } from "./fetcher";

export type Risk = "access" | "money" | "personal-data" | "standard";
export interface CapabilityItem {
  capability: string;
  description: string;
  group: string;
  title: string;
  risk: Risk;
  operations: string[];
  assignable: boolean;
}
export interface ClientManifest {
  client_name: string;
  grant_types: string[];
  scope?: string;
  capabilities?: string[];
  token_endpoint_auth_method?: "private_key_jwt" | "none";
  redirect_uris?: string[];
  jwks?: { keys: Record<string, unknown>[] };
  jwks_uri?: string;
  first_party?: boolean;
  "x-emdur-reasons"?: Record<string, string>;
  end_user_signup?: EndUserSignup | null;
}
/** Client metadata that lets a confidential client sign its own end users up (contract EndUserSignup). Only a
 * platform administrator may set it; the console passes it through and shows it, never edits it. */
export interface EndUserSignup {
  rp_id: string;
  web_origins: string[];
  minimum_age: number;
  terms_uri: string;
  policy_uri: string;
  session_days?: number;
  daily_code_limit?: number;
}
export interface Client extends ClientManifest {
  id: string;
  client_id: string;
  organization_id: string;
  name: string;
  scope: string;
  capabilities: string[];
  scopes: string[];
  kind: "web" | "native" | "service";
  state: "active" | "revoked";
  first_party: boolean;
  created_at: string;
  last_used_at?: string;
  token_endpoint_auth_method: "private_key_jwt" | "none";
  redirect_uris: string[];
}
export interface ClientDiff {
  added: string[];
  removed: string[];
  redirectsAdded: string[];
  redirectsRemoved: string[];
  keysChanged: boolean;
  nameChanged: boolean;
}
export const clientsPath = (org: string) =>
  `/v1/organizations/${encodeURIComponent(org)}/clients`;
export const clientPath = (org: string, client: string) =>
  `${clientsPath(org)}/${encodeURIComponent(client)}`;
export const cataloguePath = (org: string) =>
  `/v1/organizations/${encodeURIComponent(org)}/capabilities`;
export const appHref = (org: string, client?: string) =>
  `/orgs/${encodeURIComponent(org)}/apps${client ? `/${encodeURIComponent(client)}` : ""}`;

/** The compiled contract checks shapes without Function/eval. Public-key and URL checks strengthen it. */
export function parseManifest(text: string): ClientManifest {
  const value = manifestJson(text);
  if (!validateManifest(value))
    throw new Error(
      "This manifest does not match the app registration format. Check its name, permissions, grants, redirects and public keys.",
    );
  const data = value as ClientManifest & { name?: string; scopes?: string[] };
  if (data.jwks && data.token_endpoint_auth_method !== "none")
    publicKeySet(data.jwks);
  for (const address of [
    ...(data.redirect_uris ?? []),
    ...(data.jwks_uri ? [data.jwks_uri] : []),
    ...(data.end_user_signup
      ? [data.end_user_signup.terms_uri, data.end_user_signup.policy_uri]
      : []),
  ]) {
    publicHttpsAddress(address);
  }
  if (
    data.grant_types.includes("authorization_code") &&
    !data.redirect_uris?.length
  )
    throw new Error(
      "A sign-in app needs at least one redirect address in its manifest.",
    );
  const sets = [data.scope?.split(" "), data.capabilities, data.scopes].filter(
    (set): set is string[] => set !== undefined,
  );
  if (
    sets.some(
      (set) =>
        set.length !== new Set(set).size ||
        set.some((capability) => !CAPABILITY.test(capability)),
    )
  )
    throw new Error("List each permission once, separated by spaces.");
  if (
    sets.some(
      (set) => [...set].sort().join(" ") !== [...sets[0]!].sort().join(" "),
    )
  )
    throw new Error("The manifest's permission lists must agree.");
  if (data.name && data.client_name && data.name !== data.client_name)
    throw new Error("The manifest's names must agree.");
  return data.client_name
    ? data
    : { ...data, client_name: data.name!, capabilities: data.scopes };
}
export function manifestJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("Paste a valid JSON manifest.");
  }
}

/** End-user sign-up as sentences for the person reviewing it. Android origins are the app's signing key, so they
 * read as "its Android app"; the contract's defaults fill what the metadata leaves out. */
export function endUserSignupText(signup: EndUserSignup): string {
  const web = signup.web_origins.filter(
    (origin) => !origin.startsWith("android:"),
  );
  const origins = [
    ...web,
    ...(web.length < signup.web_origins.length ? ["its Android app"] : []),
  ];
  const from =
    origins.length > 1
      ? `${origins.slice(0, -1).join(", ")} and ${origins.at(-1)}`
      : origins[0];
  return [
    `Signs people up on ${signup.rp_id} from ${from}.`,
    `People must be ${signup.minimum_age} or older.`,
    `Sessions last up to ${signup.session_days ?? 90} days.`,
    `Terms: ${signup.terms_uri}.`,
    `Privacy: ${signup.policy_uri}.`,
    ...(signup.daily_code_limit
      ? [
          `Sends up to ${signup.daily_code_limit.toLocaleString("en")} sign-in code emails a day.`,
        ]
      : []),
  ].join(" ");
}

/** Proposal arguments are an arbitrary object in the contract; only sentences are explanations. */
export function clientReasons(value: unknown): Record<string, string> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? Object.fromEntries(
        Object.entries(value).filter(
          (entry): entry is [string, string] => typeof entry[1] === "string",
        ),
      )
    : {};
}
export const permissionsOf = (value: ClientManifest): string[] =>
  value.capabilities ?? value.scope?.split(" ").filter(Boolean) ?? [];
export const addedTo = (before: string[], after: string[]) =>
  [...new Set(after)].filter((value) => !before.includes(value)).sort();
export function diffClients(
  before: ClientManifest,
  after: ClientManifest,
): ClientDiff {
  return {
    added: addedTo(permissionsOf(before), permissionsOf(after)),
    removed: addedTo(permissionsOf(after), permissionsOf(before)),
    redirectsAdded: addedTo(
      before.redirect_uris ?? [],
      after.redirect_uris ?? [],
    ),
    redirectsRemoved: addedTo(
      after.redirect_uris ?? [],
      before.redirect_uris ?? [],
    ),
    keysChanged:
      JSON.stringify(before.jwks ?? before.jwks_uri) !==
      JSON.stringify(after.jwks ?? after.jwks_uri),
    nameChanged: before.client_name !== after.client_name,
  };
}
export function riskCounts(
  permissions: string[],
  catalogue: CapabilityItem[],
): Record<Risk | "unknown", number> {
  const counts = {
    access: 0,
    money: 0,
    "personal-data": 0,
    standard: 0,
    unknown: 0,
  };
  for (const capability of new Set(permissions))
    counts[
      catalogue.find((item) => item.capability === capability)?.risk ??
        "unknown"
    ]++;
  return counts;
}
export function permissionSummary(
  diff: ClientDiff,
  catalogue: CapabilityItem[],
): string {
  const counts = riskCounts(diff.added, catalogue);
  return `${diff.added.length} added, ${diff.removed.length} removed${counts.access ? ` · ${counts.access} grants access` : ""}${counts.money ? " · grants money access" : ""}${counts["personal-data"] ? " · uses personal data" : ""}${counts.unknown ? " · includes a permission this console doesn't recognise yet" : ""}`;
}
export async function readClient(
  fetcher: FetchLike,
  org: string,
  client: string,
) {
  return readVersioned<Client>(fetcher, clientPath(org, client), "app");
}
export async function updateClient(
  fetcher: FetchLike,
  org: string,
  client: string,
  patch: Patch,
  etag: string,
  key: string,
) {
  const answer = await patchVersioned<Client | Proposal>(
    fetcher,
    clientPath(org, client),
    patch, etag, key,
  );
  return answer.status === 202
    ? { kind: "proposal" as const, proposal: answer.data as Proposal }
    : {
        kind: "client" as const,
        client: answer.data as Client,
        etag: answer.etag,
      };
}

const CAPABILITY = /^[a-z_]+\.[a-z_]+:[a-z_]+$/;

export type ServiceClientBody = {
  name: string;
  grant_types: ["client_credentials"];
  jwks: { keys: object[] };
  scopes: string[];
};

export function scopesFrom(text: string): string[] {
  return text.split(/[\s,]+/).filter(Boolean);
}

export function capabilitiesFrom(text: string): string[] {
  const chosen = [...new Set(scopesFrom(text))].sort();
  if (chosen.length === 0)
    throw new Error("Give the client at least one capability.");
  const wrong = chosen.filter((s) => !CAPABILITY.test(s));
  if (wrong.length)
    throw new Error(
      `Not a capability (module.resource:action): ${wrong.join(", ")}`,
    );
  return chosen;
}

export function serviceClient(
  name: string,
  jwks: { keys: object[] },
  scopesText: string,
): ServiceClientBody {
  return {
    name,
    grant_types: ["client_credentials"],
    jwks,
    scopes: capabilitiesFrom(scopesText),
  };
}
